/**
 * authErrorConstants.ts
 * ---------------------
 * Centralized constants for the authentication response-handling flow:
 *  - AUTH_MESSAGE_DESCRIPTIONS: stable backend `messageDescription` codes the
 *    client branches on.
 *  - AUTH_MODAL_TITLES / AUTH_MODAL_MESSAGES: copy shown in the blocking modals.
 *  - AUTH_TOAST_MESSAGES: copy shown in toasts.
 *
 * Keeping all user-facing strings here makes them easy to review, translate,
 * and keep consistent with the guide (docs/Auth_Response_Handling_React_Guide).
 */

/** Stable backend `messageDescription` codes used for branching. */
export const AUTH_MESSAGE_DESCRIPTIONS = {
  // 400
  AUTH_HEADER_INVALID: 'AUTH_HEADER_INVALID',
  AUTH_TOKEN_MALFORMED: 'AUTH_TOKEN_MALFORMED',
  // 401
  AUTH_TOKEN_MISSING: 'AUTH_TOKEN_MISSING',
  INVALID_TOKEN: 'INVALID_TOKEN',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  SESSION_INVALIDATED: 'SESSION_INVALIDATED',
  // 503
  AUTH_SERVICE_UNAVAILABLE: 'AUTH_SERVICE_UNAVAILABLE',
  // 500 (known auth-internal)
  AUTH_VALIDATION_INTERNAL_ERROR: 'AUTH_VALIDATION_INTERNAL_ERROR',
  AUTH_INTERNAL_ERROR: 'AUTH_INTERNAL_ERROR',
} as const;

/** Descriptions treated as "known auth-internal" 500s (retryable platform toast). */
export const KNOWN_500_DESCRIPTIONS: string[] = [
  AUTH_MESSAGE_DESCRIPTIONS.AUTH_VALIDATION_INTERNAL_ERROR,
  AUTH_MESSAGE_DESCRIPTIONS.AUTH_INTERNAL_ERROR,
];

/** Modal titles. */
export const AUTH_MODAL_TITLES = {
  AUTH_SERVICE_DOWN: 'Authentication Unavailable',
  SESSION_ENDED: 'Session Ended',
  MULTI_DEVICE_RESTRICTION: 'Multi-Device Restriction',
} as const;

/** Modal body messages. */
export const AUTH_MODAL_MESSAGES = {
  AUTH_SERVICE_DOWN: 'Authentication service is currently down. Please try again later.',
  MULTI_DEVICE_RESTRICTION: 'Unauthorized access from multiple devices detected. Please log in again to continue.',
  // Forced "back to login" messages, keyed by backend messageDescription.
  LOGIN_REQUIRED: {
    [AUTH_MESSAGE_DESCRIPTIONS.AUTH_TOKEN_MALFORMED]:
      'Your session is corrupted. Please log in again to continue.',
    [AUTH_MESSAGE_DESCRIPTIONS.INVALID_TOKEN]:
      'Your session is no longer valid. Please log in again to continue.',
    [AUTH_MESSAGE_DESCRIPTIONS.AUTH_TOKEN_MISSING]:
      'Your session could not be verified. Please log in again to continue.',
    [AUTH_MESSAGE_DESCRIPTIONS.SESSION_EXPIRED]:
      'Your session has expired. Please log in again to continue.',
    [AUTH_MESSAGE_DESCRIPTIONS.SESSION_INVALIDATED]:
      'Your session has ended (you may have signed in elsewhere). Please log in again.',
  } as Record<string, string>,
  LOGIN_REQUIRED_FALLBACK: 'Your session has ended. Please log in again to continue.',
} as const;

/** Toast messages (base text; a "(Ref: <id>)" suffix is appended when available). */
export const AUTH_TOAST_MESSAGES = {
  SERVICE_UNAVAILABLE: 'Your request could not be processed at this time. Please verify the information provided and try again. If the issue persists, contact support with Reference ID.',
  PLATFORM_ERROR: 'Something went wrong on our side. Please try again.',
} as const;
